import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import create from 'zustand';

import { useGlobalContext } from '../../../../../context/GlobalContext';
import useMetronomeEngine from '../../../../../hooks/useMetronomeEngine';
import MetronomeVolumeModal from '../../../../../components/modals/MetronomeVolumeModal';
import { getSetting, setSetting, settingKeys } from '../../../../../utils/settings';

import { ReactComponent as PlayIcon } from '../../../../../icons/play-solid.svg';
import { ReactComponent as SquareIcon } from '../../../../../icons/square.svg';
import Icon from '../../../../../components/common/Icon';

import { resolveSoundType } from '../../../../../utils/metronomeClick';
import { SUBDIVISIONS } from '../../../../../utils/metronomeSchedule';

import './MetronomePlugin.scss';

// Load volume from localStorage or default to 0.5 (50%)
const loadVolume = () => {
  const stored = getSetting(settingKeys.metronomeVolume);
  return stored !== undefined ? stored : 0.5;
};

// Load sound type from localStorage; one never picked, or since removed, is the default
const loadSoundType = () => resolveSoundType(getSetting(settingKeys.metronomeSound));

// Load accent beat from localStorage or default to true (accent ON)
const loadAccentBeat = () => {
  const stored = getSetting(settingKeys.metronomeAccentBeat);
  return stored !== undefined ? stored : true;
};

// Clicks per beat; 1 is no subdivision
const loadSubdivision = () => {
  const stored = getSetting(settingKeys.metronomeSubdivision);
  return SUBDIVISIONS.includes(stored) ? stored : 1;
};

export const useMetronomeStore = create((set, get) => ({
  activeMetronomeId: null,
  activeWorkerRef: null,
  activeBpm: null,
  isFlashing: false,
  volume: loadVolume(),
  soundType: loadSoundType(),
  accentBeat: loadAccentBeat(),
  subdivision: loadSubdivision(),
  setSubdivision: (subdivision) => {
    setSetting(settingKeys.metronomeSubdivision, subdivision);
    set({ subdivision });
  },
  setVolume: (volume) => {
    setSetting(settingKeys.metronomeVolume, volume);
    set({ volume });
  },
  setSoundType: (soundType) => {
    setSetting(settingKeys.metronomeSound, soundType);
    set({ soundType });
  },
  setAccentBeat: (accentBeat) => {
    setSetting(settingKeys.metronomeAccentBeat, accentBeat);
    set({ accentBeat });
  },
  setActiveMetronome: (id, workerRef, bpm) => {
    // Stop any previously active metronome
    const { activeWorkerRef: prevWorker, activeMetronomeId: prevId } = get();

    if (prevWorker && prevId !== id) {
      prevWorker.postMessage({ action: 'stop' });
    }

    set({ activeMetronomeId: id, activeWorkerRef: workerRef, activeBpm: bpm });
  },
  setIsFlashing: (flashing) => set({ isFlashing: flashing }),
  setActiveBpm: (bpm) => set({ activeBpm: bpm }),
  stopMetronome: (id) => {
    const { activeMetronomeId, activeWorkerRef } = get();

    // Only stop if this is the active metronome
    if (activeMetronomeId === id && activeWorkerRef) {
      activeWorkerRef.postMessage({ action: 'stop' });
      set({ activeMetronomeId: null, activeWorkerRef: null, activeBpm: null, isFlashing: false });
    }
  },
  stopAllMetronomes: () => {
    const { activeWorkerRef } = get();
    if (activeWorkerRef) {
      activeWorkerRef.postMessage({ action: 'stop' });
      set({ activeMetronomeId: null, activeWorkerRef: null, activeBpm: null, isFlashing: false });
    }
  },
}));

const MetronomePlugin = ({ song, compact, disabled = false }) => {
  const { t } = useTranslation();
  const [showVolumeModal, setShowVolumeModal] = useState(false);
  const metronomeIdRef = useRef(`metronome-${song?._id || 'compact'}-${Math.random()}`);
  const previousIsScrollingRef = useRef(false);

  const { isScrolling } = useGlobalContext();

  const engine = useMetronomeEngine(metronomeIdRef.current);

  // Use metronome store for global state management so this control stays in
  // sync with any other metronome control (e.g. SuperMetronome) that's actually
  // driving playback.
  const activeMetronomeId = useMetronomeStore((state) => state.activeMetronomeId);
  const activeBpm = useMetronomeStore((state) => state.activeBpm);
  const globalIsFlashing = useMetronomeStore((state) => state.isFlashing);
  const volume = useMetronomeStore((state) => state.volume);
  const soundType = useMetronomeStore((state) => state.soundType);
  const setVolume = useMetronomeStore((state) => state.setVolume);
  const setSoundType = useMetronomeStore((state) => state.setSoundType);
  const stopAllMetronomes = useMetronomeStore((state) => state.stopAllMetronomes);

  const bpm = song?.bpm;
  const isDisabled = disabled || !bpm || bpm <= 0;
  const isPlaying = activeMetronomeId !== null;
  const bpmToShow = activeBpm || bpm || '--';

  // Update BPM and timeSignature when song changes
  useEffect(() => {
    if (isPlaying && bpm > 0) {
      engine.updateBPM(bpm, song?.timeSignature || '4/4');
    }
  }, [bpm, isPlaying, song?.timeSignature, engine]);

  // Follow auto-scroll setting - start/stop metronome with auto-scroll
  useEffect(() => {
    const followAutoScroll = getSetting(settingKeys.metronomeFollowAutoScroll);
    if (!followAutoScroll || isDisabled) return;

    const scrollStarted = isScrolling && !previousIsScrollingRef.current;
    const scrollStopped = !isScrolling && previousIsScrollingRef.current;

    if (scrollStarted && !isPlaying) {
      engine.start(bpm, song?.timeSignature || '4/4');
    } else if (scrollStopped && isPlaying) {
      stopAllMetronomes();
    }

    previousIsScrollingRef.current = isScrolling;
  }, [isScrolling, isPlaying, isDisabled, bpm, song?.timeSignature, engine, stopAllMetronomes]);

  const handleToggleMetronome = () => {
    if (isDisabled) return;

    if (isPlaying) {
      stopAllMetronomes();
    } else {
      engine.start(bpm, song?.timeSignature || '4/4');
    }
  };

  const handleOpenVolumeModal = () => {
    setShowVolumeModal(true);
  };

  const handleCloseVolumeModal = () => {
    setShowVolumeModal(false);
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
  };

  const handleSoundTypeChange = (newSoundType) => {
    setSoundType(newSoundType);
  };

  // Compact view for service bar - always visible
  if (compact) {
    return (
      <div className="metronome-plugin metronome-plugin--compact">
        <span
          className={`metronome-plugin__compact-label ${isPlaying ? 'active' : ''} ${
            globalIsFlashing ? 'flashing' : ''
          }`}
          onClick={handleToggleMetronome}
          title={
            isPlaying
              ? t('view.stopMetronomeAt', { bpm: activeBpm })
              : t('view.startMetronomeAt', { bpm: song?.bpm || '--' })
          }
        >
          {bpmToShow}
        </span>
      </div>
    );
  }

  // Original view
  return (
    <>
      <div className={`song-plugin metronome-plugin ${isDisabled ? 'disabled' : ''}`}>
        <span className="plugin__title clickable-link" onClick={handleOpenVolumeModal}>
          {t('view.metronome')}
        </span>
        <span className="plugin__controls">
          <span
            className={`btn-play-stop ${isDisabled ? 'disabled' : ''} ${globalIsFlashing ? 'flashing' : ''}`}
            onClick={handleToggleMetronome}
            title={
              isDisabled ? t('view.noBpm') : t(isPlaying ? 'view.stopMetronomeAt' : 'view.startMetronomeAt', { bpm })
            }
          >
            <Icon
              icon={isPlaying ? <SquareIcon /> : <PlayIcon />}
              className={`metronome-icon ${globalIsFlashing ? 'flash' : ''}`}
            />
          </span>
          <span className={`display-value ${isDisabled ? 'disabled' : ''}`}>{bpmToShow}</span>
        </span>
      </div>
      <MetronomeVolumeModal
        isOpen={showVolumeModal}
        volume={volume}
        soundType={soundType}
        onVolumeChange={handleVolumeChange}
        onSoundTypeChange={handleSoundTypeChange}
        onClose={handleCloseVolumeModal}
      />
    </>
  );
};

MetronomePlugin.propTypes = {
  song: PropTypes.shape({
    bpm: PropTypes.number,
  }),
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default MetronomePlugin;
